import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
  type QueryFunction,
  type QueryKey,
} from '@tanstack/react-query';

type DefaultError = unknown;

type OptionsWithoutKeyFn<TQueryFnData, TError, TData, TQueryKey extends QueryKey> = Omit<
  UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  'queryKey' | 'queryFn'
>;

export function createQuery<
  TQueryFnData,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  base: {
    queryKey: TQueryKey;
    queryFn: QueryFunction<TQueryFnData, TQueryKey>;
  } & OptionsWithoutKeyFn<TQueryFnData, TError, TData, TQueryKey>,
): (
  options?: OptionsWithoutKeyFn<TQueryFnData, TError, TData, TQueryKey>,
) => UseQueryResult<TData, TError>;

export function createQuery<
  TParams extends unknown[],
  TQueryFnData,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  builder: (...params: [...TParams]) => {
    queryKey: TQueryKey;
    queryFn: () => Promise<TQueryFnData> | TQueryFnData;
  } & OptionsWithoutKeyFn<TQueryFnData, TError, TData, TQueryKey>,
): (
  args: Readonly<[...TParams]>,
  options?: OptionsWithoutKeyFn<TQueryFnData, TError, TData, TQueryKey>,
) => UseQueryResult<TData, TError>;

export function createQuery(arg: any): any {
  if (typeof arg !== 'function') {
    return (options) =>
      useQuery({
        ...arg,
        ...options,
        queryKey: arg.queryKey,
        queryFn: arg.queryFn,
      });
  }

  return (args, options) => {
    if (!Array.isArray(args)) {
      throw new Error('At least one parameter is required');
    }

    const cfg = arg(...args);
    const { queryKey, queryFn, ...builtOptions } = cfg;

    return useQuery({
      ...builtOptions,
      ...options,
      queryKey,
      queryFn,
    });
  };
}
